import { useUpdateTheme } from '@/api/glow-up/glow-up.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Theme, UpdateThemeRequest } from '@repo/types/src/glow-up';
import { Edit } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type EditThemeProps = {
  theme: Theme;
  refetch: () => void;
};

export const EditTheme = ({ theme, refetch }: EditThemeProps) => {
  const mutation = useUpdateTheme();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: theme.title,
    description: theme.description,
  });

  const handleUpdate = () => {
    if (!formData.title || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const payload: UpdateThemeRequest = {
      title: formData.title,
      description: formData.description,
    };

    mutation.mutate(
      { id: theme.id, payload },
      {
        onSuccess: (data) => {
          setOpen(false);
          refetch();
          toast.success(`Le thème ${data.title} a été modifié avec succès!`, {
            duration: 3000,
          });
        },
        onError: () => {
          toast.error(`Erreur lors de la modification du thème`, {
            duration: 3000,
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">Modifier le thème</DialogTitle>
          <DialogDescription className="text-center">
            Modifier les informations du thème
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-4">
          <div>
            <Input
              type="text"
              placeholder="Titre du thème"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
            />
          </div>

          <div>
            <Input
              type="text"
              placeholder="Description du thème"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost">Annuler</Button>
          </DialogClose>
          <Button variant="default" onClick={handleUpdate}>
            Modifier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
